/**
 * Target Focus content: level params, shape set, seeded target/distractors and object spawn.
 * Spec: Canvas-rendered moving targets + distractors; tap only the specified shape type.
 */

export const SHAPE_IDS = [
  "star",
  "heart",
  "circle",
  "triangle",
  "square",
  "diamond",
  "cross",
  "moon",
  "pentagon",
  "hexagon",
] as const;

export type ShapeId = (typeof SHAPE_IDS)[number];

export interface LevelParams {
  speedPxPerSec: number;
  targetCount: number;
  distractorCount: number;
  distractorTypeCount: number;
  roundDurationMs: number;
  sizeDecreaseMidRound: boolean;
  camouflageOpacity: number;
}

export interface MovingObject {
  id: string;
  type: "target" | "distractor";
  shapeId: ShapeId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  removedAt?: number;
  baseRadius?: number;
}

export interface TargetAndDistractors {
  targetShapeId: ShapeId;
  distractorShapeIds: ShapeId[];
}

const ROUND_DURATION_MS = 33000;

/** Seeded RNG in [0, 1) for deterministic rounds */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

/** Level params per spec: 1-2 targets only slow; 3-4 one distractor; 5-6 two distractors medium; 7-8 size decrease; 9-10 camouflage */
export function getLevelParams(difficulty: number): LevelParams {
  const level = Math.max(1, Math.min(10, difficulty));
  if (level <= 2) {
    return {
      speedPxPerSec: 50,
      targetCount: 5,
      distractorCount: 0,
      distractorTypeCount: 0,
      roundDurationMs: ROUND_DURATION_MS,
      sizeDecreaseMidRound: false,
      camouflageOpacity: 1,
    };
  }
  if (level <= 4) {
    return {
      speedPxPerSec: 50,
      targetCount: 5,
      distractorCount: 3,
      distractorTypeCount: 1,
      roundDurationMs: ROUND_DURATION_MS,
      sizeDecreaseMidRound: false,
      camouflageOpacity: 1,
    };
  }
  if (level <= 6) {
    return {
      speedPxPerSec: 80,
      targetCount: 5,
      distractorCount: 3,
      distractorTypeCount: 2,
      roundDurationMs: ROUND_DURATION_MS,
      sizeDecreaseMidRound: false,
      camouflageOpacity: 1,
    };
  }
  if (level <= 8) {
    return {
      speedPxPerSec: 80,
      targetCount: 5,
      distractorCount: 4,
      distractorTypeCount: 3,
      roundDurationMs: ROUND_DURATION_MS,
      sizeDecreaseMidRound: true,
      camouflageOpacity: 1,
    };
  }
  return {
    speedPxPerSec: 110,
    targetCount: 5,
    distractorCount: 5,
    distractorTypeCount: 3,
    roundDurationMs: ROUND_DURATION_MS,
    sizeDecreaseMidRound: false,
    camouflageOpacity: 0.6,
  };
}

/** Deterministic: pick target and distractor shape IDs from seed and level */
export function resolveTargetAndDistractors(
  seed: string,
  level: number
): TargetAndDistractors {
  const rng = seededRandom(seed);
  const params = getLevelParams(level);
  const ids = [...SHAPE_IDS];
  // Shuffle indices by seed
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  const targetShapeId = ids[0] as ShapeId;
  const distractorShapeIds: ShapeId[] = [];
  const n = params.distractorTypeCount;
  for (let i = 1; i <= n && i < ids.length; i++) {
    distractorShapeIds.push(ids[i] as ShapeId);
  }
  return { targetShapeId, distractorShapeIds };
}

/** Velocity with given speed (px/s); random direction */
function randomVelocity(rng: () => number, speedPxPerSec: number): { vx: number; vy: number } {
  const angle = rng() * Math.PI * 2;
  return {
    vx: Math.cos(angle) * speedPxPerSec,
    vy: Math.sin(angle) * speedPxPerSec,
  };
}

/** Create a single target for respawn (random position/velocity, non-deterministic) */
export function createRespawnedTarget(
  shapeId: ShapeId,
  layoutW: number,
  layoutH: number,
  speedPxPerSec: number,
  id: string,
  baseRadius: number = 28
): MovingObject {
  const angle = Math.random() * Math.PI * 2;
  const vx = Math.cos(angle) * speedPxPerSec;
  const vy = Math.sin(angle) * speedPxPerSec;
  const x = baseRadius + Math.random() * Math.max(1, layoutW - baseRadius * 2);
  const y = baseRadius + Math.random() * Math.max(1, layoutH - baseRadius * 2);
  return {
    id,
    type: "target",
    shapeId,
    x,
    y,
    vx,
    vy,
    radius: baseRadius,
    opacity: 1,
    baseRadius,
  };
}

/** Spawn objects with seeded positions and velocities */
export function spawnObjects(
  seed: string,
  level: number,
  canvasW: number,
  canvasH: number,
  targetShapeId: ShapeId,
  distractorShapeIds: ShapeId[]
): MovingObject[] {
  const rng = seededRandom(seed);
  const params = getLevelParams(level);
  const objects: MovingObject[] = [];
  const baseRadius = 28;
  let idCounter = 0;

  const add = (type: "target" | "distractor", shapeId: ShapeId) => {
    const x = baseRadius + rng() * (canvasW - baseRadius * 2);
    const y = baseRadius + rng() * (canvasH - baseRadius * 2);
    const { vx, vy } = randomVelocity(rng, params.speedPxPerSec);
    objects.push({
      id: `obj-${idCounter++}`,
      type,
      shapeId,
      x,
      y,
      vx,
      vy,
      radius: baseRadius,
      opacity: type === "target" && params.camouflageOpacity < 1 ? params.camouflageOpacity : 1,
      baseRadius,
    });
  };

  for (let i = 0; i < params.targetCount; i++) {
    add("target", targetShapeId);
  }
  const perDistractor = Math.ceil(params.distractorCount / Math.max(1, distractorShapeIds.length));
  for (const shapeId of distractorShapeIds) {
    for (let i = 0; i < perDistractor; i++) {
      add("distractor", shapeId);
    }
  }

  return objects;
}

/** Draw a shape on canvas (ctx already scaled by dpr; x,y,radius in layout px) */
export function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeId: ShapeId,
  x: number,
  y: number,
  radius: number,
  opacity: number,
  isTarget: boolean
): void {
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = isTarget ? "#1976d2" : "#757575";
  ctx.strokeStyle = isTarget ? "#0d47a1" : "#424242";
  ctx.lineWidth = 2;

  switch (shapeId) {
    case "circle":
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    case "star": {
      ctx.beginPath();
      const points = 5;
      for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? radius : radius * 0.4;
        const a = (i * Math.PI) / points - Math.PI / 2;
        const px = x + r * Math.cos(a);
        const py = y + r * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "heart": {
      ctx.beginPath();
      const s = radius / 1.2;
      ctx.moveTo(x, y + s * 0.3);
      ctx.bezierCurveTo(x, y - s * 0.5, x - s, y - s * 0.5, x - s, y + s * 0.3);
      ctx.bezierCurveTo(x - s, y + s, x, y + s * 1.2, x, y + s * 1.2);
      ctx.bezierCurveTo(x, y + s * 1.2, x + s, y + s, x + s, y + s * 0.3);
      ctx.bezierCurveTo(x + s, y - s * 0.5, x, y - s * 0.5, x, y + s * 0.3);
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "triangle": {
      ctx.beginPath();
      ctx.moveTo(x, y - radius);
      ctx.lineTo(x + radius, y + radius * 0.7);
      ctx.lineTo(x - radius, y + radius * 0.7);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "square":
      ctx.fillRect(x - radius * 0.85, y - radius * 0.85, radius * 1.7, radius * 1.7);
      ctx.strokeRect(x - radius * 0.85, y - radius * 0.85, radius * 1.7, radius * 1.7);
      break;
    case "diamond": {
      ctx.beginPath();
      ctx.moveTo(x, y - radius);
      ctx.lineTo(x + radius, y);
      ctx.lineTo(x, y + radius);
      ctx.lineTo(x - radius, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "cross": {
      const w = radius * 0.35;
      ctx.fillRect(x - radius, y - w, radius * 2, w * 2);
      ctx.fillRect(x - w, y - radius, w * 2, radius * 2);
      ctx.strokeRect(x - radius, y - w, radius * 2, w * 2);
      ctx.strokeRect(x - w, y - radius, w * 2, radius * 2);
      break;
    }
    case "moon": {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0.5 * Math.PI, 1.5 * Math.PI);
      ctx.arc(x + radius * 0.4, y, radius * 0.6, 1.5 * Math.PI, 0.5 * Math.PI);
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "pentagon": {
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i * Math.PI * 2) / 5 - Math.PI / 2;
        const px = x + radius * Math.cos(a);
        const py = y + radius * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "hexagon": {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI * 2) / 6 - Math.PI / 6;
        const px = x + radius * Math.cos(a);
        const py = y + radius * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    }
    default:
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
  }

  ctx.restore();
}

/**
 * Per-round scoring for Target Focus.
 *
 * The round ends when the level timer expires; at that moment we have:
 *  - `correctTaps`: taps that removed a target shape in time.
 *  - `falseTaps`: taps on a distractor or on empty canvas.
 *  - `missedTargets`: target shapes still alive when the round ended.
 *
 * Every opportunity counts toward the denominator (including misses), so a
 * player who ignores everything scores 0 — we do not reward inaction.
 */
export interface TargetFocusRoundInput {
  correctTaps: number;
  falseTaps: number;
  missedTargets: number;
}

export interface TargetFocusRoundMetrics {
  accuracy: number;
  errorCount: number;
  total: number;
}

export function computeTargetFocusRoundMetrics(
  input: TargetFocusRoundInput,
): TargetFocusRoundMetrics {
  const { correctTaps, falseTaps, missedTargets } = input;
  const total = correctTaps + falseTaps + missedTargets;
  const accuracy = total > 0 ? correctTaps / total : 0;
  return { accuracy, errorCount: falseTaps + missedTargets, total };
}
